import { useCallback, useMemo, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { UsersFilterBar } from './UsersFilterBar';
import { UsersTable } from './UsersTable';
import { UsersTableSkeleton } from './UsersTableSkeleton';
import { MobileUserCard } from './MobileUserCard';
import { MobileCardSkeleton } from '../../../components/common/MobileCardSkeleton/MobileCardSkeleton';
import { ListPageLayout } from '../components/ListPageLayout';
import { UsersFilterState } from '../../../types/dashboardV2';
import { useUsersQuery } from '../../../hooks/useDashboardQueries';
import { useListPage } from '../../../hooks/useListPage';
import { useActiveFilterCount } from '../../../hooks/useActiveFilterCount';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import {
  fromMobileUsersFilters,
  toMobileUsersFilters,
} from './usersMobileFilters';

const MobileFilterSheet = lazy(() => import('./MobileUsersFilterSheet'));

import type { MobileUsersFilterState } from '../../../types/mobileFilterSheetTypes';

const INITIAL_FILTERS: UsersFilterState = {
  search: '',
  role: '',
  status: '',
  dateRange: '',
  hasActiveSession: false,
  hasTelegram: false,
};

export const UsersV2 = () => {
  const { t } = useTranslation();
  const {
    page,
    rowsPerPage,
    filters,
    queryFilters,
    isFilterSheetOpen,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    refresh: handleRefresh,
    openFilterSheet,
    closeFilterSheet,
  } = useListPage<UsersFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.USERS,
    urlSync: true,
  });

  const {
    data,
    isLoading: loading,
    isFetching,
    error,
  } = useUsersQuery({ page: page + 1, pageSize: rowsPerPage, filters: queryFilters });

  const users = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleApplyMobileFilters = useCallback(
    (mobile: MobileUsersFilterState) =>
      handleFilterChange(fromMobileUsersFilters(mobile)),
    [handleFilterChange],
  );

  const mobileFiltersForSheet = useMemo(
    () => toMobileUsersFilters(filters),
    [filters],
  );

  const errorMessage = error instanceof Error ? error.message : t('dashboard.users.errorLoading');
  const activeFilterCount = useActiveFilterCount(filters, INITIAL_FILTERS);

  return (
    <ListPageLayout
      filterBar={
        <UsersFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
        />
      }
      showLoading={loading || isFetching}
      isLoading={loading}
      hasError={Boolean(error)}
      errorMessage={errorMessage}
      onRetry={handleRefresh}
      countText={t('dashboard.users.displayingOfTotal', { count: users.length, total })}
      itemCount={users.length}
      emptyState={{ icon: 'group', message: t('dashboard.users.noUsers') }}
      table={
        <UsersTable
          users={users}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      }
      tableSkeleton={<UsersTableSkeleton />}
      mobileTitle={t('dashboard.sidebar.users')}
      mobileCountText={t('dashboard.users.allUsersWithCount', { count: total })}
      search={{
        value: filters.search,
        onChange: (search) => handleFilterChange({ search }),
        placeholder: t('dashboard.users.searchPlaceholder'),
        inputProps: { type: 'text', name: 'mobile-user-search' },
      }}
      mobileCards={users.map((user) => (
        <MobileUserCard key={user.id} user={user} />
      ))}
      mobileSkeleton={<MobileCardSkeleton showFooter={false} />}
      pagination={{
        total,
        page,
        rowsPerPage,
        onPageChange: handlePageChange,
        onRowsPerPageChange: handleRowsPerPageChange,
      }}
      filterButton={{ activeCount: activeFilterCount, onOpen: openFilterSheet }}
      isFilterSheetOpen={isFilterSheetOpen}
      filterSheet={
        <MobileFilterSheet
          isOpen={isFilterSheetOpen}
          onClose={closeFilterSheet}
          onApply={handleApplyMobileFilters}
          initialFilters={mobileFiltersForSheet}
        />
      }
    />
  );
};

export default UsersV2;
